import React, { useState, useEffect } from "react";
import { Trash2, StickyNote } from "lucide-react";
import AssessmentProfileService from "../../services/AssessmentProfileService";
import { removeProfileFromQuestion } from "../../utils";

const ProfileQuestions = ({ profileId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const questions =
          await AssessmentProfileService.getQuestions(profileId);
        setQuestions(questions);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch questions");
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [profileId]);

  const handleRemoveQuestion = async (questionId) => {
    if (!window.confirm("Are you sure?")) {
      return false;
    }
    await removeProfileFromQuestion(questionId);
    setQuestions(questions.filter((q) => q.id !== questionId));
  };

  const renderQuestionContent = (question) => {
    return (
      <div className="question-content p-4">
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Type: {question.question_type}
          </p>
          <p className="text-sm text-gray-600">
            Difficulty: {question.difficulty_level} | Importance:{" "}
            {question.importance}
          </p>
          {question.category && (
            <p className="text-sm text-gray-600">
              Category: {question.category}
            </p>
          )}
        </div>

        {question.context && (
          <div className="mb-3">
            <h4 className="font-medium text-gray-800">Context</h4>
            <p className="text-sm">{question.context}</p>
          </div>
        )}

        {question.answer_type && (
          <div className="mb-3">
            <h4 className="font-medium text-gray-800">Answer Type</h4>
            <p className="text-sm">{question.answer_type}</p>
          </div>
        )}

        {question.options && question.options.length > 0 && (
          <div className="mb-3">
            <h4 className="font-medium text-gray-800">Options</h4>
            <ul className="list-disc pl-5">
              {question.options.map((option, idx) => (
                <li key={idx} className="text-sm">
                  {option}
                </li>
              ))}
            </ul>
          </div>
        )}

        {question.model_answer && (
          <div className="mb-3">
            <h4 className="font-medium text-gray-800">Model Answer</h4>
            <p className="text-sm">{question.model_answer}</p>
          </div>
        )}

        {question.evaluation_criteria &&
          question.evaluation_criteria.length > 0 && (
            <div className="mb-3">
              <h4 className="font-medium text-gray-800">Evaluation Criteria</h4>
              <ul className="list-disc pl-5">
                {question.evaluation_criteria.map((criterion, idx) => (
                  <li key={idx} className="text-sm">
                    {criterion}
                  </li>
                ))}
              </ul>
            </div>
          )}

        {question.follow_up_questions &&
          question.follow_up_questions.length > 0 && (
            <div className="mb-3">
              <h4 className="font-medium text-gray-800">Follow-up Questions</h4>
              <ul className="list-disc pl-5">
                {question.follow_up_questions.map((followUp, idx) => (
                  <li key={idx} className="text-sm">
                    <p>
                      Condition: {followUp.condition} - Value:{" "}
                      {followUp.condition_value}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </div>
    );
  };

  if (loading)
    return <div className="text-center py-4">Loading questions...</div>;
  if (error)
    return <div className="text-center py-4 text-red-500">{error}</div>;
  if (questions.length === 0)
    return (
      <div className="text-center py-4">
        No questions found for this profile.
      </div>
    );

  return (
    <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md">
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-primary-50 to-white">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <StickyNote className="h-5 w-5 mr-2 text-primary-600" />
          Assessment Questions
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Questions added to the profile
        </p>
      </div>
      <div className="space-y-4 border-t border-gray-200 px-4 py-5 sm:p-6">
        {questions.map((question) => (
          <div
            key={question.id}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white"
          >
            <div className="accordion-item">
              <details className="group">
                <summary className="flex justify-between items-center p-4 cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium">
                      {question.question_text}
                    </h3>
                    <div className="flex gap-2 mt-1">
                      {question.tags &&
                        question.tags.map((tag, idx) =>
                          tag !== "" ? (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                            >
                              {tag}
                            </span>
                          ) : (
                            ""
                          ),
                        )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveQuestion(question.id);
                      }}
                      className="p-1 rounded-full hover:bg-red-100 text-red-500"
                      title="Remove question"
                    >
                      <Trash2 size={18} />
                    </button>
                    <span className="transform group-open:rotate-180 transition-transform">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </div>
                </summary>

                {renderQuestionContent(question)}
              </details>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileQuestions;
